/*
  bb-quick-complete-modal.js
  ───────────────────────────────────────────────────────────────────────
  Standalone controller for the "Quick Complete" bundle modal.
  Markup: snippets/bb-quick-complete-modal.liquid
  CSS:    assets/mix-bundle.css

  Communicates with the per-section IIFE (sections/mix-bundle-product.liquid)
  via custom events dispatched on the section root:
    Listens for:
      - bb:request-quick-modal     → open the modal (detail.triggerEl optional)
      - bb:selections-changed      → re-sync rows + CTA price/enable
      - bb:cart-add-success        → close modal, reset busy state
      - bb:cart-add-error          → show error inside modal, stay open
    Dispatches:
      - bb:request-cart-submit     → asks section IIFE to call addToCart()
                                     (detail.btn = the modal CTA button)

  Critical safeguards:
    A) cloneNode subtree gets id/name/for/aria-* namespaced (no collisions
       with the live carousel swatches)
    C) DocumentFragment populate → single layout/paint
    D) replaceChildren() before every populate → no stale content / listeners
    E) .is-loading class during submit → CSS pointer-events:none on body
    F) ALL listeners attached ONCE per modal instance
*/
(function () {
  "use strict";

  if (window.__sgBbQuickModalLoaded) return;
  window.__sgBbQuickModalLoaded = true;

  /* /en storefront: English UI strings. Color option values stay Hebrew in the
     data attributes (selection logic); sgColorDisp() maps them for display only. */
  var SG_EN = /^en\b/i.test(document.documentElement.lang || "");
  var SG_EN_COLORS = { "ורוד": "Pink", "סגול": "Purple", "תכלת": "Light Blue", "כחול": "Blue", "לבן": "White", "שחור": "Black", "אדום": "Red", "כסף": "Silver", "כסוף": "Silver", "ורוד פודרה": "Powder Pink", "קרמל עדין": "Soft Caramel", "שמנת": "Cream", "קרם": "Cream", "ירוק": "Green" };
  function sgColorDisp(name) {
    if (!SG_EN || !name) return name;
    var key = String(name).trim();
    return Object.prototype.hasOwnProperty.call(SG_EN_COLORS, key) ? SG_EN_COLORS[key] : name;
  }

  /* Defensively namespace id/name/for/aria-* on cloned subtrees so the
     modal's cloned .bb-swatch nodes can never collide with the carousel
     originals (same name = same radio group, duplicate id = label-for
     misroutes). Suffix is unique per modal-open generation. */
  function namespaceAttrs(node, suffix) {
    var attrs = ["id", "name", "for", "aria-controls", "aria-labelledby", "aria-describedby"];
    function patch(el) {
      if (!el || !el.hasAttribute) return;
      for (var i = 0; i < attrs.length; i++) {
        var a = attrs[i];
        if (el.hasAttribute(a)) {
          el.setAttribute(a, el.getAttribute(a) + suffix);
        }
      }
    }
    patch(node);
    var all = node.querySelectorAll ? node.querySelectorAll("*") : [];
    for (var j = 0; j < all.length; j++) patch(all[j]);
  }

  /* Prefer currentSrc (resolved by the browser) over src (may be unset
     on lazy-loaded images). */
  function thumbSrc(slide) {
    var imgEl = slide.querySelector(".bb-product__img");
    if (!imgEl) return "";
    return imgEl.currentSrc || imgEl.getAttribute("src") || imgEl.getAttribute("data-src") || "";
  }

  /* Try to read selections + expected from a snapshot the section IIFE
     attaches to the section root. Fallback: walk the DOM for visual cues. */
  function readSelectionsFromRoot(ROOT, EXPECTED) {
    var snap = {};
    var slides = ROOT.querySelectorAll(".bb-product[data-product-idx]");
    slides.forEach(function (slide) {
      var idx = parseInt(slide.getAttribute("data-product-idx"), 10);
      if (isNaN(idx) || idx >= EXPECTED) return;
      var sel = slide.querySelector(".bb-swatch.is-selected");
      if (sel) {
        snap[idx] = {
          variantId: parseInt(sel.getAttribute("data-variant-id"), 10) || 0,
          colorName: sel.getAttribute("data-color-name") || "",
          imgSrc: sel.getAttribute("data-image") || ""
        };
      }
    });
    return snap;
  }

  function buildRow(ROOT, idx, nsSuffix, selections) {
    var slide = ROOT.querySelector('.bb-product[data-product-idx="' + idx + '"]');
    if (!slide) return null;

    var titleEl = slide.querySelector(".bb-product__title");
    var title = titleEl ? (titleEl.textContent || "").trim() : "";
    if (!title) title = SG_EN ? "Item" : "מוצר";

    var existing = selections[idx];

    var row = document.createElement("div");
    row.className = "bb-qcm-row";
    row.setAttribute("data-bb-qcm-row", "");
    row.setAttribute("data-product-idx", String(idx));

    var thumbSrcStr = thumbSrc(slide);
    if (thumbSrcStr) {
      var thumb = document.createElement("img");
      thumb.className = "bb-qcm-row__thumb";
      thumb.src = thumbSrcStr;
      thumb.alt = title;
      thumb.loading = "lazy";
      thumb.decoding = "async";
      thumb.width = 56;
      thumb.height = 56;
      row.appendChild(thumb);
    } else {
      var ph = document.createElement("div");
      ph.className = "bb-qcm-row__thumb";
      ph.setAttribute("aria-hidden", "true");
      row.appendChild(ph);
    }

    var main = document.createElement("div");
    main.className = "bb-qcm-row__main";

    var titleH3 = document.createElement("h3");
    titleH3.className = "bb-qcm-row__title";
    titleH3.textContent = title;
    main.appendChild(titleH3);

    var colorName = document.createElement("p");
    colorName.className = "bb-qcm-row__color-name";
    colorName.setAttribute("data-bb-qcm-color-name", "");
    if (existing && existing.colorName) colorName.textContent = sgColorDisp(existing.colorName);
    main.appendChild(colorName);

    var swatchesWrap = document.createElement("div");
    swatchesWrap.className = "bb-qcm-row__swatches";
    swatchesWrap.setAttribute("data-bb-qcm-swatches", "");

    var sourceSwatches = slide.querySelectorAll(".bb-swatch");
    for (var s = 0; s < sourceSwatches.length; s++) {
      var clonedSw = sourceSwatches[s].cloneNode(true);
      namespaceAttrs(clonedSw, nsSuffix);

      var clonedVid = parseInt(clonedSw.getAttribute("data-variant-id"), 10);
      if (existing && existing.variantId && existing.variantId === clonedVid) {
        clonedSw.classList.add("is-selected");
        clonedSw.setAttribute("aria-pressed", "true");
      } else {
        clonedSw.classList.remove("is-selected");
        clonedSw.setAttribute("aria-pressed", "false");
      }
      swatchesWrap.appendChild(clonedSw);
    }

    main.appendChild(swatchesWrap);
    row.appendChild(main);

    if (existing && existing.variantId) {
      row.classList.add("is-complete");
    } else {
      row.classList.add("is-pulsing");
    }

    return row;
  }

  function buildContent(ROOT, modalEl, EXPECTED, selections) {
    var body = modalEl.querySelector("[data-bb-quick-modal-body]");
    if (!body) return;
    var nsSuffix = "-bbqcm-" + Date.now();
    var frag = document.createDocumentFragment();
    for (var idx = 0; idx < EXPECTED; idx++) {
      var row = buildRow(ROOT, idx, nsSuffix, selections);
      if (row) frag.appendChild(row);
    }
    if (typeof body.replaceChildren === "function") {
      body.replaceChildren(frag);
    } else {
      body.innerHTML = "";
      body.appendChild(frag);
    }
  }

  function initModal(modalEl) {
    if (modalEl.__bbInit) return;
    modalEl.__bbInit = true;

    /* CRITICAL: ROOT must be the SAME element the section IIFE uses
       (`document.getElementById('main-configurator')`). The section listens
       for bb:request-cart-submit on #main-configurator - if we dispatch on
       the .shopify-section ancestor instead, the event bubbles UP (away
       from the listener) and never fires. Result: modal CTA silently fails.
       Prefer the modal's own ancestor lookup so we still work if multiple
       configurators ever ship on one page. */
    var ROOT = modalEl.closest("#main-configurator") ||
               document.getElementById("main-configurator") ||
               modalEl.closest(".shopify-section") ||
               document.documentElement;
    var EXPECTED = 5;
    var lastTrigger = null;
    var escBound = false;
    var modalCta = modalEl.querySelector("[data-bb-quick-modal-cta]");
    var modalCtaText = modalCta && modalCta.querySelector("[data-bb-quick-modal-cta-text]");
    var modalCtaPrice = modalCta && modalCta.querySelector("[data-bb-quick-modal-cta-price]");
    var modalCtaSep = modalCta && modalCta.querySelector("[data-bb-quick-modal-cta-sep]");
    var modalErrorEl = modalEl.querySelector("[data-bb-quick-modal-error]");
    var origCtaText = modalCtaText ? modalCtaText.textContent : (SG_EN ? "Add to cart" : "הוסיפי לעגלה");

    function open(triggerEl) {
      if (modalEl.classList.contains("is-open")) return;
      buildContent(ROOT, modalEl, EXPECTED, readSelectionsFromRoot(ROOT, EXPECTED));
      lastTrigger = triggerEl || null;
      modalEl.setAttribute("aria-hidden", "false");
      modalEl.removeAttribute("inert"); /* B9: keep inert in sync with aria-hidden */
      modalEl.classList.add("is-open");
      document.body.classList.add("bb-qcm-lock");
      var closeBtn = modalEl.querySelector("[data-bb-quick-modal-close]");
      if (closeBtn && typeof closeBtn.focus === "function") {
        setTimeout(function () { try { closeBtn.focus(); } catch (e) {} }, 50);
      }
    }

    function close() {
      if (!modalEl.classList.contains("is-open")) return;
      modalEl.classList.remove("is-open", "is-loading");
      modalEl.setAttribute("aria-hidden", "true");
      modalEl.setAttribute("inert", ""); /* B9 */
      document.body.classList.remove("bb-qcm-lock");
      if (lastTrigger && typeof lastTrigger.focus === "function") {
        try { lastTrigger.focus(); } catch (e) {}
      }
      lastTrigger = null;
    }

    /* Close handlers */
    var closeBtn = modalEl.querySelector("[data-bb-quick-modal-close]");
    if (closeBtn) {
      closeBtn.addEventListener("click", function (e) { e.preventDefault(); close(); });
    }
    var backdrop = modalEl.querySelector("[data-bb-quick-modal-backdrop]");
    if (backdrop) {
      backdrop.addEventListener("click", function (e) { e.preventDefault(); close(); });
    }
    if (!escBound) {
      escBound = true;
      document.addEventListener("keydown", function (e) {
        if ((e.key === "Escape" || e.keyCode === 27) && modalEl.classList.contains("is-open")) close();
      });
    }

    /* Open request from section IIFE */
    ROOT.addEventListener("bb:request-quick-modal", function (e) {
      EXPECTED = (e && e.detail && e.detail.expected) || EXPECTED;
      open(e && e.detail && e.detail.triggerEl);
    });

    /* Swatch picks - only update modal-specific UI here.
       The actual selectVariant() call is handled by the section's existing
       ROOT click handler, which catches all .bb-swatch clicks (modal too,
       since modal lives inside the section root). */
    modalEl.addEventListener("click", function (e) {
      if (modalEl.classList.contains("is-loading")) return;
      var sw = e.target.closest(".bb-swatch");
      if (!sw || sw.disabled) return;
      var col = sw.getAttribute("data-color-name") || "";
      var row = sw.closest("[data-bb-qcm-row]");
      if (row) {
        var colorNameEl = row.querySelector("[data-bb-qcm-color-name]");
        if (colorNameEl) colorNameEl.textContent = sgColorDisp(col);
        row.classList.add("is-complete");
        row.classList.remove("is-pulsing");
      }
    });

    /* Live sync - fires from section's refreshAllStates() */
    ROOT.addEventListener("bb:selections-changed", function (e) {
      if (!modalEl.classList.contains("is-open")) return;
      if (!e || !e.detail) return;
      var sels = e.detail.selections || {};
      EXPECTED = e.detail.expected || EXPECTED;
      var isComplete = !!e.detail.isComplete;

      var rows = modalEl.querySelectorAll("[data-bb-qcm-row]");
      rows.forEach(function (row) {
        var idx = parseInt(row.getAttribute("data-product-idx"), 10);
        if (isNaN(idx)) return;
        var sel = sels[idx];
        var colorNameEl = row.querySelector("[data-bb-qcm-color-name]");
        if (sel && sel.variantId) {
          if (colorNameEl) colorNameEl.textContent = sgColorDisp(sel.colorName) || "";
          row.classList.add("is-complete");
          row.classList.remove("is-pulsing");
          var swatches = row.querySelectorAll(".bb-swatch");
          swatches.forEach(function (sw) {
            var vid = parseInt(sw.getAttribute("data-variant-id"), 10);
            var match = vid === sel.variantId;
            sw.classList.toggle("is-selected", match);
            sw.setAttribute("aria-pressed", match ? "true" : "false");
          });
        } else {
          if (colorNameEl) colorNameEl.textContent = "";
          row.classList.remove("is-complete");
          row.classList.add("is-pulsing");
          var swatches2 = row.querySelectorAll(".bb-swatch.is-selected");
          swatches2.forEach(function (sw) {
            sw.classList.remove("is-selected");
            sw.setAttribute("aria-pressed", "false");
          });
        }
      });

      if (modalCta) {
        modalCta.disabled = !isComplete;
        modalCta.classList.toggle("is-ready", isComplete);
      }
      /* Mirror price from the main CTA */
      var mainBtn = ROOT.querySelector(".bb-cta__btn[data-bb-atc]");
      if (modalCtaPrice && mainBtn) {
        var srcPrice = mainBtn.querySelector("[data-bb-btn-price]");
        var srcSep = mainBtn.querySelector("[data-bb-btn-sep]");
        if (srcPrice) modalCtaPrice.textContent = srcPrice.textContent || "";
        if (modalCtaSep && srcSep) modalCtaSep.hidden = srcSep.hidden;
      }
    });

    /* Submit - delegate to section IIFE via event */
    if (modalCta) {
      modalCta.addEventListener("click", function (e) {
        e.preventDefault();
        if (modalCta.disabled) return;
        if (modalEl.classList.contains("is-loading")) return;

        if (modalErrorEl) { modalErrorEl.textContent = ""; modalErrorEl.hidden = true; }

        modalEl.classList.add("is-loading");
        modalCta.setAttribute("aria-busy", "true");
        if (modalCtaText) modalCtaText.textContent = SG_EN ? "Adding to cart…" : "מוסיפה לסל…";
        if (modalCtaSep) modalCtaSep.hidden = true;
        if (modalCtaPrice) modalCtaPrice.textContent = "";

        ROOT.dispatchEvent(new CustomEvent("bb:request-cart-submit", {
          detail: { btn: modalCta }
        }));
      });
    }

    /* Cart success → close */
    ROOT.addEventListener("bb:cart-add-success", function () {
      modalEl.classList.remove("is-loading");
      if (modalCta) modalCta.removeAttribute("aria-busy");
      if (modalCtaText) modalCtaText.textContent = origCtaText;
      close();
    });

    /* Cart error → show inside modal, stay open */
    ROOT.addEventListener("bb:cart-add-error", function (e) {
      if (!modalEl.classList.contains("is-open")) return;
      var msg = (e && e.detail && e.detail.message) || (SG_EN ? "Something went wrong. Please try again." : "משהו השתבש. נסי שוב.");
      modalEl.classList.remove("is-loading");
      if (modalCta) modalCta.removeAttribute("aria-busy");
      if (modalCtaText) modalCtaText.textContent = origCtaText;
      if (modalErrorEl) {
        modalErrorEl.textContent = msg;
        modalErrorEl.hidden = false;
      }
    });

    /* Focus trap */
    modalEl.addEventListener("keydown", function (e) {
      if (!modalEl.classList.contains("is-open")) return;
      var isTab = e.key === "Tab" || e.keyCode === 9;
      if (!isTab) return;
      var focusables = modalEl.querySelectorAll(
        'button:not([disabled]):not([tabindex="-1"]), [href]:not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      var firstEl = focusables[0];
      var lastEl = focusables[focusables.length - 1];
      var active = document.activeElement;
      if (e.shiftKey) {
        if (active === firstEl || !modalEl.contains(active)) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (active === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    });
  }

  function bootAll() {
    var modals = document.querySelectorAll("[data-bb-quick-modal]");
    modals.forEach(initModal);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootAll);
  } else {
    bootAll();
  }
})();
